import { useState, useCallback } from 'react';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { useCreateDroneMutation } from '../adminTools.mutations';
import type { CreateDroneResponse } from '../types/adminTools.types';

export interface UseAddDroneReturn {
  // Dialog state
  dialogOpen: boolean;
  loading: boolean;
  result: CreateDroneResponse | null;
  error: string;
  // Form state
  serialNumber: string;
  model: string;
  // Actions
  openDialog: () => void;
  closeDialog: () => void;
  addDrone: () => Promise<void>;
  // Setters
  setSerialNumber: (value: string) => void;
  setModel: (value: string) => void;
}

export function useAddDrone(): UseAddDroneReturn {
  const { showError, showSuccess } = useErrorHandler();
  const createDroneMutation = useCreateDroneMutation();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<CreateDroneResponse | null>(null);
  const [error, setError] = useState('');

  // Form state
  const [serialNumber, setSerialNumber] = useState('');
  const [model, setModel] = useState('');

  const openDialog = useCallback(() => {
    setDialogOpen(true);
    setSerialNumber('');
    setModel('');
    setResult(null);
    setError('');
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setSerialNumber('');
    setModel('');
    setResult(null);
    setError('');
  }, []);

  const addDrone = useCallback(async () => {
    if (!serialNumber.trim()) {
      setError('Serial number is required');
      return;
    }

    setResult(null);
    setError('');

    try {
      const response = await createDroneMutation.mutateAsync({
        serial_number: serialNumber.trim(),
        model: model.trim() || undefined,
      });

      setResult(response);

      if (response.success) {
        showSuccess(`Drone ${serialNumber} added successfully`);
        // Clear form on success but keep dialog open to show success message
        setSerialNumber('');
        setModel('');
      }
    } catch (err) {
      showError(err, 'Failed to add drone');
      setError('Failed to add drone');
    }
  }, [serialNumber, model, createDroneMutation, showError, showSuccess]);

  return {
    dialogOpen,
    loading: createDroneMutation.isPending,
    result,
    error,
    serialNumber,
    model,
    openDialog,
    closeDialog,
    addDrone,
    setSerialNumber,
    setModel,
  };
}
